import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ArrowLeft, AlertTriangle, Link2, X, User2, IdCard } from 'lucide-react';
import {
  adminDismissSuggestion, adminLinkSuggestion, adminListLinkSuggestions,
  adminListClubsCatalog, adminListTeamsCatalog, adminGetUser,
  friendlyError,
} from '@/lib/api';
import { resolvePlayerAvatar } from '@/lib/playerAvatar';
import { toast } from 'sonner';

const PROFILE_TYPE_LABEL = {
  player: 'Jugador', family: 'Familia', follower: 'Aficionado',
  club_admin: 'Admin de club', scout: 'Scout', staff: 'Staff', admin: 'Administrador',
};
const MATCH_STRENGTH_LABEL = {
  strong: 'Fuerte (nombre + apellidos + email)',
  email: 'Media (email coincide)',
  weak: 'Débil (solo nombre y apellidos)',
};
const MATCH_STRENGTH_CLASS = {
  strong: 'bg-emerald-100 text-emerald-800 border border-emerald-200',
  email: 'bg-[var(--ns-amber-50)] text-[var(--ns-gold-700)] border border-[var(--ns-amber-200)]',
  weak: 'bg-[var(--ns-bg-2)] text-[var(--ns-text-muted)] border border-[var(--ns-border)]',
};

export default function AdminPlayerLinkSuggestions() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmId, setConfirmId] = useState(null);
  // Datos derivados para el modal (fetched sólo cuando se abre)
  const [modalDetail, setModalDetail] = useState(null); // { userDetail }
  const [modalLoading, setModalLoading] = useState(false);
  const [linking, setLinking] = useState(false);
  // Catálogos club/team para resolver nombre legible en el modal.
  const [clubs, setClubs] = useState([]);
  const [teams, setTeams] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [res, clubsList, teamsList] = await Promise.all([
        adminListLinkSuggestions({ status: 'pending' }),
        adminListClubsCatalog(),
        adminListTeamsCatalog(),
      ]);
      setItems(res.items || []);
      setClubs(clubsList.items || []);
      setTeams(teamsList.items || []);
    } catch (err) { toast.error(friendlyError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const clubById = useMemo(() => Object.fromEntries(clubs.map((c) => [c.id, c])), [clubs]);
  const teamById = useMemo(() => Object.fromEntries(teams.map((t) => [t.id, t])), [teams]);

  // Sugerencia actualmente seleccionada para confirmar.
  const currentSuggestion = useMemo(
    () => items.find((s) => s.id === confirmId) || null,
    [confirmId, items],
  );

  const openConfirm = async (suggestion) => {
    setConfirmId(suggestion.id);
    setModalDetail(null);
    setModalLoading(true);
    try {
      // `profile_type` no viene en el listado de sugerencias; se lee desde el
      // detalle de usuario (endpoint admin ya existente). Sin nuevos endpoints.
      const userDetail = suggestion.user?.id ? await adminGetUser(suggestion.user.id) : null;
      setModalDetail({ userDetail });
    } catch (err) {
      setModalDetail({ userDetail: null });
      toast.error(friendlyError(err));
    } finally {
      setModalLoading(false);
    }
  };
  const closeConfirm = () => {
    if (linking) return;
    setConfirmId(null);
    setModalDetail(null);
  };

  const doLink = async () => {
    if (!confirmId || linking) return;
    setLinking(true);
    try {
      await adminLinkSuggestion(confirmId);
      toast.success('Ficha vinculada.');
      setConfirmId(null);
      setModalDetail(null);
      await load();
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setLinking(false);
    }
  };
  const doDismiss = async (id) => {
    if (!window.confirm('¿Descartar esta sugerencia? El jugador podrá crear su propia ficha si no quedan más pendientes.')) return;
    try { await adminDismissSuggestion(id); toast.success('Sugerencia descartada.'); await load(); }
    catch (err) { toast.error(friendlyError(err)); }
  };

  // Derivados legibles para el modal (a partir de datos ya disponibles).
  const dialogData = useMemo(() => {
    if (!currentSuggestion) return null;
    const s = currentSuggestion;
    const u = s.user || {};
    const p = s.profile || {};
    const club = p.club_id ? clubById[p.club_id] : null;
    const team = p.team_id ? teamById[p.team_id] : null;
    const teamLine = team
      ? `${team.name}${team.season ? ` · ${team.season}` : ''}${team.category ? ` (${team.category})` : ''}`
      : '—';
    const userSurnames = [modalDetail?.userDetail?.first_surname, modalDetail?.userDetail?.second_surname]
      .filter(Boolean).join(' ') || u.last_name || '—';
    const userFullName = `${(modalDetail?.userDetail?.first_name || u.first_name || '').trim()} ${userSurnames}`.trim() || '—';
    const profSurnames = [p.first_surname, p.second_surname].filter(Boolean).join(' ') || p.last_name || '—';
    const profFullName = `${(p.first_name || '').trim()} ${profSurnames}`.trim() || '—';
    return {
      user: {
        fullName: userFullName,
        email: modalDetail?.userDetail?.email || u.email || '—',
        profileType: modalDetail?.userDetail?.profile_type
          ? (PROFILE_TYPE_LABEL[modalDetail.userDetail.profile_type] || modalDetail.userDetail.profile_type)
          : null,
      },
      profile: {
        fullName: profFullName,
        email: p.email || '—',
        clubName: club?.name || '—',
        teamLine,
      },
      matchStrength: s.match_strength || null,
    };
  }, [currentSuggestion, clubById, teamById, modalDetail]);

  return (
    <AdminLayout>
      <div className="flex flex-col gap-5">
        <Button asChild variant="ghost" size="sm" className="w-fit text-[var(--ns-text-muted)]">
          <Link to="/admin/fichas-jugadores" className="inline-flex items-center gap-1"><ArrowLeft className="h-4 w-4" /> Volver al listado</Link>
        </Button>
        <div>
          <h1 className="font-display uppercase tracking-tight text-2xl sm:text-3xl text-[var(--ns-text)]">
            Sugerencias pendientes de vinculación
          </h1>
          <p className="text-sm text-[var(--ns-text-muted)] mt-1">
            Coincidencias exactas por nombre y apellidos detectadas al registrarse el usuario. La coincidencia por nombre no garantiza que sea el mismo jugador.
          </p>
        </div>

        {loading ? (
          <Skeleton className="h-44 w-full rounded-2xl" />
        ) : items.length === 0 ? (
          <Card data-testid="admin-suggestions-empty"
            className="p-8 rounded-2xl bg-white border border-dashed border-[var(--ns-border-strong)] text-center">
            <p className="text-sm text-[var(--ns-text-muted)]">No hay sugerencias pendientes.</p>
          </Card>
        ) : (
          <ul className="space-y-3">
            {items.map((s) => {
              const avatar = resolvePlayerAvatar(s.profile || {});
              return (
                <li key={s.id} data-testid={`admin-suggestion-${s.id}`}
                  className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 rounded-xl bg-white border border-[var(--ns-amber-200)]">
                  <div className="h-16 w-16 rounded-xl overflow-hidden bg-[var(--ns-bg-2)] flex items-center justify-center shrink-0">
                    {avatar ? <img src={avatar} alt="" className="h-full w-full object-cover" /> : <span className="text-xs text-[var(--ns-text-subtle)]">—</span>}
                  </div>
                  <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                    <div className="min-w-0">
                      <p className="text-[10px] uppercase tracking-widest text-[var(--ns-text-subtle)]">Cuenta</p>
                      <p className="text-sm font-semibold text-[var(--ns-text)] truncate">{s.user?.first_name} {s.user?.last_name}</p>
                      <p className="text-xs text-[var(--ns-text-muted)] truncate">{s.user?.email}</p>
                    </div>
                    <div className="min-w-0">
                      <p className="text-[10px] uppercase tracking-widest text-[var(--ns-text-subtle)]">Ficha candidata</p>
                      <p className="text-sm font-semibold text-[var(--ns-text)] truncate">{s.profile?.first_name} {s.profile?.last_name}</p>
                      <p className="text-xs text-[var(--ns-text-muted)] truncate">
                        {s.profile?.position || '—'} · nac. {s.profile?.date_of_birth || '—'}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={() => openConfirm(s)} size="sm"
                      data-testid={`admin-suggestion-link-${s.id}`}
                      className="h-9 rounded-lg bg-gradient-to-r from-[var(--ns-gold)] to-[var(--ns-gold-600)] text-[#1c1612] font-semibold hover:from-[var(--ns-gold-600)] hover:to-[var(--ns-gold-700)] hover:text-white border-0">
                      <Link2 className="h-3.5 w-3.5 mr-1" /> Vincular
                    </Button>
                    <Button onClick={() => doDismiss(s.id)} size="sm" variant="outline"
                      data-testid={`admin-suggestion-dismiss-${s.id}`}
                      className="h-9 rounded-lg">
                      <X className="h-3.5 w-3.5 mr-1" /> Descartar
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        <AlertDialog open={!!confirmId} onOpenChange={(o) => !o && closeConfirm()}>
          <AlertDialogContent data-testid="admin-suggestion-confirm-dialog" className="sm:max-w-lg">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-[var(--ns-gold-700)]" />
                Confirmar vinculación
              </AlertDialogTitle>
              <AlertDialogDescription>
                Revisa que la cuenta y la ficha corresponden a la misma persona. La coincidencia por nombre y apellidos no garantiza que sea el mismo jugador. Al confirmar, la ficha quedará vinculada al usuario y el resto de sugerencias pendientes para esa cuenta se descartarán.
              </AlertDialogDescription>
            </AlertDialogHeader>

            {modalLoading || !dialogData ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Skeleton className="h-36 rounded-xl" />
                <Skeleton className="h-36 rounded-xl" />
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="admin-suggestion-confirm-body">
                <section
                  className="rounded-xl border border-[var(--ns-border)] bg-white p-3 flex flex-col gap-1"
                  data-testid="admin-suggestion-confirm-user"
                >
                  <p className="text-[10px] uppercase tracking-widest text-[var(--ns-text-subtle)] inline-flex items-center gap-1">
                    <User2 className="h-3 w-3" /> Cuenta de usuario
                  </p>
                  <p className="text-sm font-semibold text-[var(--ns-text)] break-words" data-testid="dlg-user-name">
                    {dialogData.user.fullName}
                  </p>
                  <p className="text-xs text-[var(--ns-text-muted)] break-all" data-testid="dlg-user-email">
                    {dialogData.user.email}
                  </p>
                  {dialogData.user.profileType ? (
                    <p className="text-xs text-[var(--ns-text-muted)]" data-testid="dlg-user-profile-type">
                      Tipo de perfil: <span className="text-[var(--ns-text)] font-medium">{dialogData.user.profileType}</span>
                    </p>
                  ) : null}
                </section>

                <section
                  className="rounded-xl border border-[var(--ns-border)] bg-white p-3 flex flex-col gap-1"
                  data-testid="admin-suggestion-confirm-profile"
                >
                  <p className="text-[10px] uppercase tracking-widest text-[var(--ns-text-subtle)] inline-flex items-center gap-1">
                    <IdCard className="h-3 w-3" /> Ficha de jugador
                  </p>
                  <p className="text-sm font-semibold text-[var(--ns-text)] break-words" data-testid="dlg-profile-name">
                    {dialogData.profile.fullName}
                  </p>
                  <p className="text-xs text-[var(--ns-text-muted)] break-all" data-testid="dlg-profile-email">
                    {dialogData.profile.email}
                  </p>
                  <p className="text-xs text-[var(--ns-text-muted)]" data-testid="dlg-profile-club">
                    Club: <span className="text-[var(--ns-text)] font-medium">{dialogData.profile.clubName}</span>
                  </p>
                  <p className="text-xs text-[var(--ns-text-muted)]" data-testid="dlg-profile-team">
                    Equipo/categoría: <span className="text-[var(--ns-text)] font-medium">{dialogData.profile.teamLine}</span>
                  </p>
                </section>
              </div>
            )}

            {dialogData?.matchStrength ? (
              <div className="flex items-center gap-2 pt-1" data-testid="admin-suggestion-confirm-strength">
                <span className="text-[10px] uppercase tracking-widest text-[var(--ns-text-subtle)]">Coincidencia:</span>
                <Badge className={`text-[11px] ${MATCH_STRENGTH_CLASS[dialogData.matchStrength] || ''}`}>
                  {MATCH_STRENGTH_LABEL[dialogData.matchStrength] || dialogData.matchStrength}
                </Badge>
              </div>
            ) : null}

            <AlertDialogFooter>
              <AlertDialogCancel disabled={linking} data-testid="admin-suggestion-confirm-cancel">
                Cancelar
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={doLink}
                disabled={linking || modalLoading}
                data-testid="admin-suggestion-confirm-yes"
                className="bg-gradient-to-r from-[var(--ns-gold)] to-[var(--ns-gold-600)] text-[#1c1612] disabled:opacity-50"
              >
                {linking ? 'Vinculando…' : 'Sí, vincular'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
}
